import { useEffect, useMemo, useState } from "react";

import {
  getMeasurementDistribution,
  matchAllPathways,
  parsePathwayDsl,
  PREDEFINED_PATTERNS,
  validatePathwayDsl,
  type DslValidationError,
  type PathwayMatch,
  type PathwayPattern,
} from "@thalamos/pathway-logic";
import type { DetentionPeriod, MhaStatusRow } from "@thalamos/pathway-logic";

import type { CurveType } from "../stats/curveFitting.js";
import { MeasurementDistributionModal } from "./MeasurementDistributionModal.js";
import { PathwayDslHelpModal } from "./PathwayDslHelpModal.js";
import { ShowExamplesButton } from "./PathwayExampleViz.js";

type PatientWithEntries = {
  id: string;
  entries: MhaStatusRow[];
  detentionPeriods: DetentionPeriod[];
};

type SavedPattern = {
  name: string;
  dsl: string;
  savedAt: number;
  isPredefined?: boolean; // true for built-in patterns that can't be deleted
};

const STORAGE_KEY = "pathway-saved-patterns";

// Convert predefined patterns to SavedPattern format
function getPredefinedPatterns(): SavedPattern[] {
  return Object.entries(PREDEFINED_PATTERNS).map(([, dsl]) => {
    const pattern = parsePathwayDsl(dsl);
    return {
      name: pattern.name,
      dsl,
      savedAt: 0,
      isPredefined: true,
    };
  });
}

function loadSavedPatterns(): SavedPattern[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const userPatterns: SavedPattern[] = stored
      ? (JSON.parse(stored) as SavedPattern[])
      : [];
    // Combine predefined patterns with user patterns
    // User patterns with same name will override predefined ones
    const predefined = getPredefinedPatterns();
    const userNames = new Set(userPatterns.map((p) => p.name.toLowerCase()));
    const nonOverriddenPredefined = predefined.filter(
      (p) => !userNames.has(p.name.toLowerCase()),
    );
    return [...nonOverriddenPredefined, ...userPatterns];
  } catch {
    return getPredefinedPatterns();
  }
}

function savePatternsToStorage(patterns: SavedPattern[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(patterns));
}

type Props = {
  patients: PatientWithEntries[];
  // Thin-client: matches are computed server-side over the whole cohort. When
  // present they replace the in-browser matchAllPathways over `patients` (which
  // is now only the loaded window and would give partial stats).
  serverMatches?: PathwayMatch[];
  serverMatchedCount?: number;
  serverTotalPatients?: number;
  // Live preview (unapplied) of the DSL currently in the editor, matched
  // server-side over the same base filters. When `previewMatches` is non-null it
  // drives the modal's count + measurements so they track what's typed; the grid
  // stays on the applied filter until Apply. Null previewMatches = no active
  // preview (fall back to serverMatches). `onPreviewDslChange` pushes the editor
  // text up to trigger the debounced preview fetch (null when the modal closes).
  previewMatches?: PathwayMatch[] | null;
  previewMatchedCount?: number;
  previewLoading?: boolean;
  onPreviewDslChange?: (dsl: string | null) => void;
  pathwayDsl?: string;
  onPathwayDslChange?: (dsl: string) => void;
  applyToGrid?: boolean;
  onApplyToGridChange?: (apply: boolean, dslText: string) => void;
  onApplyFilter?: (dslText: string) => void;
  // Measurement modal state (for URL sharing)
  expandedMeasurement?: string | null;
  onExpandedMeasurementChange?: (measurement: string | null) => void;
  measurementCurve?: CurveType;
  onMeasurementCurveChange?: (curve: CurveType) => void;
};

// Compact sidebar summary component
export const PathwaySidebarSummary = ({
  pattern,
  matches,
  allMatches,
  totalPatients,
  isActive,
  onOpenModal,
  onClearFilter,
  expandedMeasurement: controlledExpandedMeasurement,
  onExpandedMeasurementChange,
  measurementCurve,
  onMeasurementCurveChange,
}: {
  pattern: PathwayPattern | null;
  matches: { patientId: string; match: PathwayMatch }[];
  allMatches: PathwayMatch[]; // All matches for measurement aggregation
  totalPatients: number;
  isActive: boolean;
  onOpenModal: () => void;
  onClearFilter: () => void;
  // Measurement modal state (for URL sharing)
  expandedMeasurement?: string | null;
  onExpandedMeasurementChange?: (measurement: string | null) => void;
  measurementCurve?: CurveType;
  onMeasurementCurveChange?: (curve: CurveType) => void;
}) => {
  // Use controlled state if provided, otherwise local state
  const [localExpandedMeasurement, setLocalExpandedMeasurement] = useState<
    string | null
  >(null);
  const expandedMeasurement =
    controlledExpandedMeasurement !== undefined
      ? controlledExpandedMeasurement
      : localExpandedMeasurement;
  const setExpandedMeasurement = (value: string | null) => {
    if (onExpandedMeasurementChange) {
      onExpandedMeasurementChange(value);
    } else {
      setLocalExpandedMeasurement(value);
    }
  };

  const measurementStats = useMemo(() => {
    if (!pattern?.measurements) return [];
    return pattern.measurements.map((m) => {
      const dist = getMeasurementDistribution(allMatches, m.name);
      return {
        name: m.name,
        type: m.type,
        median: dist.median,
        n: dist.values.length,
      };
    });
  }, [pattern, allMatches]);

  return (
    <>
      <div
        style={{
          marginTop: "2em",
          padding: 10,
          background: isActive ? "#eef2ff" : "#f9fafb",
          borderRadius: 8,
          border: isActive ? "2px solid #4f46e5" : "1px solid #e5e7eb",
          textAlign: "center",
        }}
      >
        {isActive && pattern ? (
          <>
            {/* Pattern name - prominent */}
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#111827",
                marginBottom: 4,
                lineHeight: 1.3,
                wordWrap: "break-word",
              }}
            >
              {pattern.name}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span style={{ fontSize: 9, color: "#6b7280" }}>
                Pathway Filter
              </span>
              <button
                onClick={onOpenModal}
                style={{
                  padding: "3px 6px",
                  fontSize: 9,
                  background: "#4f46e5",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Edit
              </button>
            </div>

            {/* Match count */}
            <div style={{ fontSize: 11, color: "#374151", marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 20, color: "#059669" }}>
                {matches.length}
              </div>
              <div style={{ color: "#6b7280", fontSize: 10 }}>
                of {totalPatients} (
                {((matches.length / totalPatients) * 100).toFixed(1)}%)
              </div>
            </div>

            {/* Measurement stats - clickable to expand */}
            {measurementStats.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {measurementStats.map((stat) => (
                  <div
                    key={stat.name}
                    onClick={() => {
                      setExpandedMeasurement(stat.name);
                    }}
                    style={{
                      marginBottom: 8,
                      padding: "8px",
                      background: "rgba(79, 70, 229, 0.08)",
                      borderRadius: 4,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(79, 70, 229, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "rgba(79, 70, 229, 0.08)";
                    }}
                    title="Click to see full distribution"
                  >
                    <div
                      style={{
                        fontSize: 9,
                        color: "#6b7280",
                        marginBottom: 3,
                        lineHeight: 1.3,
                      }}
                    >
                      {stat.name}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#4f46e5",
                      }}
                    >
                      {stat.median}
                      {stat.type === "count" ? "×" : "d"}
                    </div>
                    <div style={{ fontSize: 9, color: "#9ca3af" }}>
                      median • click to expand
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={onClearFilter}
              style={{
                padding: "5px 8px",
                fontSize: 9,
                background: "#fee2e2",
                color: "#dc2626",
                border: "1px solid #fecaca",
                borderRadius: 4,
                cursor: "pointer",
                width: "100%",
              }}
            >
              Clear Filter
            </button>
          </>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                Pathway Filter
              </span>
              <button
                onClick={onOpenModal}
                style={{
                  padding: "4px 8px",
                  fontSize: 10,
                  background: "#4f46e5",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Edit
              </button>
            </div>

            {/* Patient count */}
            <div style={{ fontSize: 11, color: "#374151", marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 20, color: "#374151" }}>
                {totalPatients}
              </div>
              <div style={{ color: "#6b7280", fontSize: 10 }}>patients</div>
            </div>

            <div
              style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}
            >
              No pathway filter active
            </div>
          </>
        )}
      </div>

      {/* Expanded measurement modal */}
      {expandedMeasurement && (
        <MeasurementDistributionModal
          name={expandedMeasurement}
          matches={allMatches}
          numPatients={matches.length}
          isOpen={true}
          onClose={() => {
            setExpandedMeasurement(null);
            // Clear curve so next measurement auto-selects best fit
            onMeasurementCurveChange?.("none" as CurveType);
          }}
          initialCurveType={measurementCurve}
          onCurveTypeChange={onMeasurementCurveChange}
        />
      )}
    </>
  );
};

// Main modal component
export const PathwayAnalysisModal = ({
  patients,
  serverMatches,
  serverMatchedCount,
  serverTotalPatients,
  previewMatches,
  previewMatchedCount,
  previewLoading,
  onPreviewDslChange,
  pathwayDsl: controlledDsl,
  onPathwayDslChange,
  applyToGrid,
  onApplyToGridChange,
  onApplyFilter,
  isOpen,
  onClose,
  expandedMeasurement: controlledExpandedMeasurement,
  onExpandedMeasurementChange,
  measurementCurve,
  onMeasurementCurveChange,
}: Props & {
  isOpen: boolean;
  onClose: () => void;
}) => {
  // Local state for editing - independent of applied filter
  const [localDslText, setLocalDslText] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    DslValidationError[]
  >([]);
  const [savedPatterns, setSavedPatterns] = useState<SavedPattern[]>([]);
  const [savePatternName, setSavePatternName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // Use controlled state if provided, otherwise local state
  const [localExpandedMeasurement, setLocalExpandedMeasurement] = useState<
    string | null
  >(null);
  const expandedMeasurement =
    controlledExpandedMeasurement !== undefined
      ? controlledExpandedMeasurement
      : localExpandedMeasurement;

  const setExpandedMeasurement = (value: string | null) => {
    if (onExpandedMeasurementChange) {
      onExpandedMeasurementChange(value);
    } else {
      setLocalExpandedMeasurement(value);
    }
  };

  // Handle open/close animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Small delay to ensure the element is rendered before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300); // Match the CSS transition duration
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isOpen]);

  // Reset local edits when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLocalDslText(null);
    }
  }, [isOpen]);

  // Load saved patterns on mount
  useEffect(() => {
    setSavedPatterns(loadSavedPatterns());
  }, []);

  // Use local edits if user has made changes, otherwise use controlled value
  const dslText =
    localDslText ?? controlledDsl ?? PREDEFINED_PATTERNS.section2Readmission;
  const setDslText = setLocalDslText;

  // Check if there are unsaved local edits
  const hasLocalEdits =
    localDslText !== null &&
    localDslText !== (controlledDsl ?? PREDEFINED_PATTERNS.section2Readmission);

  // Validate and parse the DSL
  const { pattern, parseError } = useMemo(() => {
    const result = validatePathwayDsl(dslText);
    setValidationErrors(result.errors);
    if (!result.valid) {
      return {
        pattern: null,
        parseError: result.errors[0]?.message || "Invalid pattern",
      };
    }
    return { pattern: result.pattern, parseError: null };
  }, [dslText]);

  // Push the current editor DSL up for a debounced, unapplied server preview while
  // the modal is open (null when closed) so the match count + measurement
  // distributions below track what's typed, not just the last-applied filter. While
  // the pattern is mid-edit and invalid we skip the push, keeping the last good
  // preview rather than flickering the count to zero.
  useEffect(() => {
    if (!isOpen) {
      onPreviewDslChange?.(null);
      return;
    }
    if (parseError) return;
    onPreviewDslChange?.(dslText);
  }, [dslText, isOpen, parseError, onPreviewDslChange]);

  // Matches come from the server. Prefer the LIVE PREVIEW of the DSL currently in
  // the editor (matched but not applied) so the count + measurements track what's
  // typed; fall back to the applied whole-cohort matches, then to a client-side
  // match over the loaded window. `matches` is used only for its length
  // (matched-patient count), so a length-N array suffices; `allMatches` (all match
  // instances) drives the measurement stats.
  const { matches, allMatches } = useMemo(() => {
    const activeMatches = previewMatches ?? serverMatches;
    const activeCount = previewMatches
      ? (previewMatchedCount ?? 0)
      : (serverMatchedCount ?? 0);
    if (activeMatches) {
      return {
        matches: Array.from<{ patientId: string; match: PathwayMatch }>({
          length: activeCount,
        }),
        allMatches: activeMatches,
      };
    }
    if (!pattern) return { matches: [], allMatches: [] };
    const matchResults: { patientId: string; match: PathwayMatch }[] = [];
    const allMatchResults: PathwayMatch[] = [];

    for (const patient of patients) {
      // Use matchAllPathways to find patterns anywhere in the journey
      const patientMatches = matchAllPathways(
        patient.detentionPeriods,
        pattern,
      );

      const firstMatch = patientMatches[0];
      if (firstMatch !== undefined) {
        // Use first match for patient display
        matchResults.push({ patientId: patient.id, match: firstMatch });
        // Aggregate all matches for measurements
        allMatchResults.push(...patientMatches);
      }
    }
    return { matches: matchResults, allMatches: allMatchResults };
  }, [
    pattern,
    patients,
    serverMatches,
    serverMatchedCount,
    previewMatches,
    previewMatchedCount,
  ]);

  // Get all unique measurement names
  const measurementNames = useMemo(() => {
    const names = new Set<string>();
    allMatches.forEach((m) => {
      m.measurements.forEach((ms) => names.add(ms.name));
    });
    return Array.from(names);
  }, [allMatches]);

  const handleSavePattern = () => {
    if (!savePatternName.trim() || !dslText) return;
    const trimmedName = savePatternName.trim();

    // Check if pattern with same name exists
    const existingIndex = savedPatterns.findIndex(
      (p) => p.name.toLowerCase() === trimmedName.toLowerCase(),
    );

    const existingPattern = savedPatterns[existingIndex];
    if (existingPattern !== undefined) {
      const message = existingPattern.isPredefined
        ? `This will create a custom version of the built-in pattern "${trimmedName}". Continue?`
        : `A pattern named "${trimmedName}" already exists. Do you want to overwrite it?`;

      const confirmed = window.confirm(message);
      if (!confirmed) return;

      // Overwrite existing pattern (or shadow predefined with user version)
      const newPattern: SavedPattern = {
        name: trimmedName,
        dsl: dslText,
        savedAt: Date.now(),
        isPredefined: false, // User-saved version is never predefined
      };
      const updated = [...savedPatterns];
      updated[existingIndex] = newPattern;
      setSavedPatterns(updated);
      // Only save user patterns to storage
      savePatternsToStorage(updated.filter((p) => !p.isPredefined));
    } else {
      // Add new pattern
      const newPattern: SavedPattern = {
        name: trimmedName,
        dsl: dslText,
        savedAt: Date.now(),
      };
      const updated = [...savedPatterns, newPattern];
      setSavedPatterns(updated);
      // Only save user patterns to storage (not predefined ones)
      savePatternsToStorage(updated.filter((p) => !p.isPredefined));
    }

    setSavePatternName("");
    setShowSaveInput(false);
  };

  const handleDeletePattern = (index: number) => {
    const pattern = savedPatterns[index];
    // Nothing at that index, or the pattern is built in and cannot be deleted
    if (pattern === undefined || pattern.isPredefined) return;
    const updated = savedPatterns.filter((_, i) => i !== index);
    setSavedPatterns(updated);
    // Only save user patterns to storage
    savePatternsToStorage(updated.filter((p) => !p.isPredefined));
  };

  const handleLoadPattern = (pattern: SavedPattern) => {
    setDslText(pattern.dsl);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        onClick={() => {
          if (hasLocalEdits) {
            const confirmed = window.confirm(
              "You have unapplied changes. Close without applying?",
            );
            if (!confirmed) return;
          }
          onClose();
        }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          zIndex: 999,
          opacity: isAnimating ? 1 : 0,
          transition: "opacity 300ms ease-out",
          cursor: "pointer",
        }}
      />
      {/* Sidebar panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(420px, 30vw)",
          minWidth: 340,
          background: "white",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transform: isAnimating ? "translateX(0)" : "translateX(100%)",
          transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#f9fafb",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {pattern?.name || "Pathway Filter"}
            </h2>
            <span style={{ fontSize: 11, color: "#6b7280" }}>
              Pathway Analysis
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={() => {
                setShowHelp(true);
              }}
              style={{
                width: 32,
                height: 32,
                border: "none",
                background: "#e5e7eb",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 700,
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Help with pathway DSL"
            >
              ?
            </button>
            <button
              onClick={() => {
                if (hasLocalEdits) {
                  const confirmed = window.confirm(
                    "You have unapplied changes. Close without applying?",
                  );
                  if (!confirmed) return;
                }
                onClose();
              }}
              style={{
                width: 32,
                height: 32,
                border: "none",
                background: "#e5e7eb",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 18,
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Help Modal */}
        {showHelp && (
          <PathwayDslHelpModal
            onClose={() => {
              setShowHelp(false);
            }}
          />
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
          {/* Saved Patterns */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <label
                style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}
              >
                Saved Patterns
              </label>
              {!showSaveInput && (
                <button
                  onClick={() => {
                    setSavePatternName(pattern?.name || "");
                    setShowSaveInput(true);
                  }}
                  style={{
                    padding: "4px 10px",
                    fontSize: 11,
                    background: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  + Save Current
                </button>
              )}
            </div>

            {showSaveInput && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 8,
                  padding: 8,
                  background: "#ecfdf5",
                  borderRadius: 4,
                }}
              >
                <input
                  type="text"
                  placeholder="Pattern name..."
                  value={savePatternName}
                  onChange={(e) => {
                    setSavePatternName(e.target.value);
                  }}
                  style={{
                    flex: 1,
                    padding: "6px 10px",
                    fontSize: 12,
                    border: "1px solid #d1d5db",
                    borderRadius: 4,
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSavePattern();
                  }}
                  autoFocus
                />
                <button
                  onClick={handleSavePattern}
                  disabled={!savePatternName.trim()}
                  style={{
                    padding: "6px 12px",
                    fontSize: 11,
                    background: savePatternName.trim() ? "#10b981" : "#d1d5db",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor: savePatternName.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowSaveInput(false);
                    setSavePatternName("");
                  }}
                  style={{
                    padding: "6px 12px",
                    fontSize: 11,
                    background: "#e5e7eb",
                    color: "#374151",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {savedPatterns.length === 0 ? (
                <span
                  style={{
                    fontSize: 11,
                    color: "#9ca3af",
                    fontStyle: "italic",
                  }}
                >
                  No saved patterns yet
                </span>
              ) : (
                savedPatterns.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 8px",
                      background: p.isPredefined ? "#e0e7ff" : "#f3f4f6",
                      borderRadius: 4,
                      fontSize: 11,
                    }}
                  >
                    <button
                      onClick={() => {
                        handleLoadPattern(p);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: p.isPredefined ? "#4338ca" : "#4f46e5",
                        fontWeight: 500,
                        padding: 0,
                      }}
                      title={p.isPredefined ? "Built-in pattern" : undefined}
                    >
                      {p.name}
                    </button>
                    {!p.isPredefined && (
                      <button
                        onClick={() => {
                          handleDeletePattern(i);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#9ca3af",
                          fontSize: 12,
                          padding: "0 2px",
                        }}
                        title="Delete"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* DSL Editor */}
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#374151",
                display: "block",
                marginBottom: 6,
              }}
            >
              Pattern Definition
            </label>
            <textarea
              value={dslText}
              onChange={(e) => {
                setDslText(e.target.value);
              }}
              style={{
                width: "100%",
                height: 200,
                fontFamily: "monospace",
                fontSize: 11,
                padding: 10,
                border:
                  validationErrors.length > 0
                    ? "2px solid #ef4444"
                    : "1px solid #d1d5db",
                borderRadius: 6,
                resize: "vertical",
                lineHeight: 1.5,
              }}
              spellCheck={false}
            />
            {/* Error display with friendly messages */}
            {validationErrors.length > 0 && (
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 8,
                    color: "#991b1b",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  <span>⚠️</span>
                  {validationErrors.length === 1
                    ? "There's an issue with your pattern"
                    : `There are ${String(validationErrors.length)} issues with your pattern`}
                </div>
                {validationErrors.map((err, i) => {
                  // Function to render line content with highlighted error
                  const renderLineContent = () => {
                    if (!err.lineContent) return null;
                    if (!err.highlight) {
                      return (
                        <code style={{ color: "#dc2626" }}>
                          {err.lineContent}
                        </code>
                      );
                    }
                    // Find and highlight the problematic part
                    const idx = err.lineContent.indexOf(err.highlight);
                    if (idx === -1) {
                      return (
                        <code style={{ color: "#dc2626" }}>
                          {err.lineContent}
                        </code>
                      );
                    }
                    const before = err.lineContent.slice(0, idx);
                    const after = err.lineContent.slice(
                      idx + err.highlight.length,
                    );
                    return (
                      <code style={{ color: "#6b7280" }}>
                        {before}
                        <span
                          style={{
                            color: "#dc2626",
                            fontWeight: 700,
                            backgroundColor: "#fee2e2",
                            padding: "0 2px",
                            borderRadius: 2,
                          }}
                        >
                          {err.highlight}
                        </span>
                        {after}
                      </code>
                    );
                  };

                  return (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < validationErrors.length - 1 ? 10 : 0,
                        paddingBottom: i < validationErrors.length - 1 ? 10 : 0,
                        borderBottom:
                          i < validationErrors.length - 1
                            ? "1px solid #fecaca"
                            : "none",
                      }}
                    >
                      {err.lineContent && (
                        <div
                          style={{
                            fontFamily: "monospace",
                            fontSize: 10,
                            color: "#6b7280",
                            marginBottom: 4,
                          }}
                        >
                          Line {err.line}: {renderLineContent()}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 12,
                          color: "#7f1d1d",
                          marginBottom: 4,
                        }}
                      >
                        {err.friendlyMessage}
                      </div>
                      {err.suggestion && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#065f46",
                            background: "#ecfdf5",
                            padding: "6px 8px",
                            borderRadius: 4,
                            fontFamily: err.suggestion.includes("\n")
                              ? "monospace"
                              : "inherit",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          💡 {err.suggestion}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <ShowExamplesButton pattern={pattern} />
            </div>
          </div>

          {/* Match summary */}
          <div
            style={{
              padding: 16,
              background: matches.length > 0 ? "#ecfdf5" : "#fef3c7",
              borderRadius: 8,
              border: `1px solid ${matches.length > 0 ? "#10b981" : "#f59e0b"}`,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: matches.length > 0 ? "#059669" : "#d97706",
                }}
              >
                {matches.length}
              </span>
              <span
                style={{
                  fontSize: 13,
                  color: matches.length > 0 ? "#065f46" : "#92400e",
                }}
              >
                patients match
                {(serverTotalPatients ?? patients.length) > 0 && (
                  <span style={{ opacity: 0.7 }}>
                    {" "}
                    (
                    {(
                      (matches.length /
                        (serverTotalPatients ?? patients.length)) *
                      100
                    ).toFixed(1)}
                    % of {serverTotalPatients ?? patients.length})
                  </span>
                )}
              </span>
              {previewLoading && (
                <span
                  style={{
                    fontSize: 11,
                    color: "#9ca3af",
                    fontStyle: "italic",
                  }}
                >
                  updating…
                </span>
              )}
            </div>
          </div>

          {/* Apply Filter Button */}
          {onApplyFilter && (
            <div
              style={{
                padding: 16,
                background: hasLocalEdits
                  ? "#fef3c7"
                  : applyToGrid
                    ? "#ecfdf5"
                    : "#f9fafb",
                borderRadius: 8,
                border: hasLocalEdits
                  ? "2px solid #f59e0b"
                  : applyToGrid
                    ? "2px solid #10b981"
                    : "1px solid #e5e7eb",
                marginBottom: 20,
              }}
            >
              {hasLocalEdits && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 10,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#92400e",
                  }}
                >
                  <span style={{ fontSize: 14 }}>⚠️</span>
                  Unapplied changes
                </div>
              )}
              {!hasLocalEdits && applyToGrid && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 10,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#059669",
                  }}
                >
                  <span style={{ fontSize: 14 }}>✓</span>
                  Filter applied to grid
                </div>
              )}
              <button
                onClick={() => {
                  if (onPathwayDslChange) {
                    onPathwayDslChange(dslText);
                  }
                  if (onApplyToGridChange) {
                    onApplyToGridChange(true, dslText);
                  }
                  onApplyFilter(dslText);
                  setLocalDslText(null); // Reset local edits after applying
                }}
                disabled={!!parseError || (!hasLocalEdits && applyToGrid)}
                style={{
                  padding: "10px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  background: parseError
                    ? "#d1d5db"
                    : hasLocalEdits || !applyToGrid
                      ? "#f59e0b"
                      : "#d1d5db",
                  color:
                    parseError || (!hasLocalEdits && applyToGrid)
                      ? "#6b7280"
                      : "white",
                  border: "none",
                  borderRadius: 6,
                  cursor:
                    parseError || (!hasLocalEdits && applyToGrid)
                      ? "not-allowed"
                      : "pointer",
                  width: "100%",
                }}
              >
                {parseError
                  ? "Fix Errors to Apply"
                  : hasLocalEdits
                    ? "Apply Filter to Grid"
                    : !applyToGrid
                      ? "Apply Filter to Grid"
                      : "No Changes to Apply"}
              </button>
              {applyToGrid && (
                <button
                  onClick={() => {
                    if (onApplyToGridChange) {
                      onApplyToGridChange(false, "");
                    }
                  }}
                  style={{
                    marginTop: 8,
                    padding: "6px 12px",
                    fontSize: 11,
                    fontWeight: 500,
                    background: "transparent",
                    color: "#6b7280",
                    border: "1px solid #d1d5db",
                    borderRadius: 4,
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  Clear Filter
                </button>
              )}
            </div>
          )}

          {/* Measurement distributions */}
          {measurementNames.length > 0 && (
            <div>
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: 12,
                }}
              >
                Measurement Distributions
              </h3>
              {measurementNames.map((name) => {
                const measurementDef = pattern?.measurements?.find(
                  (m) => m.name === name,
                );
                return (
                  <MeasurementDistributionChart
                    key={name}
                    name={name}
                    matches={allMatches}
                    numPatients={matches.length}
                    measurementType={measurementDef?.type}
                    onExpand={() => {
                      setExpandedMeasurement(name);
                    }}
                  />
                );
              })}
            </div>
          )}

          {/* Expanded measurement modal */}
          {expandedMeasurement && (
            <MeasurementDistributionModal
              name={expandedMeasurement}
              matches={allMatches}
              numPatients={matches.length}
              measurementType={
                pattern?.measurements?.find(
                  (m) => m.name === expandedMeasurement,
                )?.type
              }
              isOpen={true}
              onClose={() => {
                setExpandedMeasurement(null);
                // Clear curve so next measurement auto-selects best fit
                onMeasurementCurveChange?.("none" as CurveType);
              }}
              initialCurveType={measurementCurve}
              onCurveTypeChange={onMeasurementCurveChange}
            />
          )}
        </div>
      </div>
    </>
  );
};

// Combined export for backward compatibility
export const PathwayAnalysis = (props: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Open modal automatically if there's a measurement to show (from URL params)
  useEffect(() => {
    if (props.expandedMeasurement) {
      setIsModalOpen(true);
    }
  }, [props.expandedMeasurement]);

  const dslText = props.pathwayDsl ?? PREDEFINED_PATTERNS.section2Readmission;

  // Parse the DSL
  const pattern = useMemo((): PathwayPattern | null => {
    try {
      return parsePathwayDsl(dslText);
    } catch {
      return null;
    }
  }, [dslText]);

  // Match against all patients - captures all matches per patient for measurements
  const { matches, allMatches } = useMemo(() => {
    if (props.serverMatches) {
      return {
        matches: Array.from<{ patientId: string; match: PathwayMatch }>({
          length: props.serverMatchedCount ?? 0,
        }),
        allMatches: props.serverMatches,
      };
    }
    if (!pattern) return { matches: [], allMatches: [] };
    const matchResults: { patientId: string; match: PathwayMatch }[] = [];
    const allMatchResults: PathwayMatch[] = [];

    for (const patient of props.patients) {
      // Use matchAllPathways to find patterns anywhere in the journey
      const patientMatches = matchAllPathways(
        patient.detentionPeriods,
        pattern,
      );

      const firstMatch = patientMatches[0];
      if (firstMatch !== undefined) {
        // Use first match for patient display
        matchResults.push({ patientId: patient.id, match: firstMatch });
        // Aggregate all matches for measurements
        allMatchResults.push(...patientMatches);
      }
    }
    return { matches: matchResults, allMatches: allMatchResults };
  }, [pattern, props.patients, props.serverMatches, props.serverMatchedCount]);

  return (
    <>
      <PathwaySidebarSummary
        pattern={pattern}
        matches={matches}
        allMatches={allMatches}
        totalPatients={props.serverTotalPatients ?? props.patients.length}
        isActive={props.applyToGrid ?? false}
        onOpenModal={() => {
          setIsModalOpen(true);
        }}
        onClearFilter={() => {
          props.onApplyToGridChange?.(false, dslText ?? "");
        }}
        expandedMeasurement={props.expandedMeasurement}
        onExpandedMeasurementChange={props.onExpandedMeasurementChange}
        measurementCurve={props.measurementCurve}
        onMeasurementCurveChange={props.onMeasurementCurveChange}
      />
      <PathwayAnalysisModal
        {...props}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
};

// Histogram component for measurement distribution
const MeasurementDistributionChart = ({
  name,
  matches,
  numPatients,
  measurementType = "duration",
  onExpand,
}: {
  name: string;
  matches: PathwayMatch[];
  numPatients: number;
  measurementType?: "duration" | "count";
  onExpand?: () => void;
}) => {
  const dist = useMemo(
    () => getMeasurementDistribution(matches, name),
    [matches, name],
  );

  // Unit suffix based on measurement type
  const unit = measurementType === "count" ? "×" : "d";
  const unitLabel = measurementType === "count" ? "occurrences" : "days";

  const downloadCsv = () => {
    const csvContent = `${name} (${unitLabel})\n` + dist.values.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name.replace(/[^a-z0-9]/gi, "_")}_measurements.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (dist.values.length === 0) return null;

  // Create histogram bins
  const range = dist.max - dist.min;
  const binCount =
    range === 0
      ? 1
      : Math.min(
          20,
          Math.max(
            Math.ceil(range) + 1,
            Math.ceil(Math.sqrt(dist.values.length)),
          ),
        );
  const binWidth = range === 0 ? 1 : Math.max(1, Math.ceil(range / binCount));
  const bins: { min: number; max: number; count: number }[] = [];

  const actualBinCount =
    Math.ceil((dist.max - dist.min + 0.001) / binWidth) || 1;

  for (let i = 0; i < actualBinCount; i++) {
    const binMin = dist.min + i * binWidth;
    const binMax = binMin + binWidth;
    const count = dist.values.filter((v) =>
      i === actualBinCount - 1
        ? v >= binMin && v <= binMax
        : v >= binMin && v < binMax,
    ).length;
    bins.push({ min: binMin, max: binMax, count });
  }

  const maxCount = Math.max(...bins.map((b) => b.count));
  const chartHeight = 70;
  const chartWidth = 240;
  const barWidth = Math.max(2, chartWidth / bins.length - 1);

  return (
    <div
      style={{
        marginBottom: 20,
        padding: 12,
        background: "#f9fafb",
        borderRadius: 6,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#374151",
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {name}
        <button
          onClick={downloadCsv}
          style={{
            padding: "2px 8px",
            fontSize: 10,
            background: "#e5e7eb",
            border: "none",
            borderRadius: 3,
            cursor: "pointer",
            color: "#6b7280",
          }}
          title="Download measurement values as CSV"
        >
          ↓ CSV
        </button>
        {onExpand && (
          <button
            onClick={onExpand}
            style={{
              padding: "2px 8px",
              fontSize: 10,
              background: "#4f46e5",
              border: "none",
              borderRadius: 3,
              cursor: "pointer",
              color: "white",
            }}
            title="Expand to larger view"
          >
            ⤢ Expand
          </button>
        )}
      </div>

      {/* Stats - prominent median */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 11, color: "#6b7280" }}>Median:</span>
        <span style={{ fontSize: 20, fontWeight: 700, color: "#4f46e5" }}>
          {dist.median}
          {unit}
        </span>
        <span style={{ fontSize: 11, color: "#9ca3af" }}>
          ({dist.values.length} pathways, {numPatients} patients)
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 4,
          marginBottom: 8,
          fontSize: 10,
        }}
      >
        <Stat label="Min" value={`${String(dist.min)}${unit}`} />
        <Stat label="P25" value={`${String(dist.p25)}${unit}`} />
        <Stat label="Med" value={`${String(dist.median)}${unit}`} />
        <Stat label="P75" value={`${String(dist.p75)}${unit}`} />
        <Stat label="Max" value={`${String(dist.max)}${unit}`} />
      </div>

      {/* Histogram */}
      <svg
        width={chartWidth + 50}
        height={chartHeight + 25}
        style={{
          overflow: "visible",
          cursor: onExpand ? "pointer" : "default",
        }}
        onClick={onExpand}
        role={onExpand ? "button" : undefined}
        tabIndex={onExpand ? 0 : undefined}
        onKeyDown={
          onExpand
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") onExpand();
              }
            : undefined
        }
      >
        {/* Y axis */}
        <line x1={35} y1={0} x2={35} y2={chartHeight} stroke="#e5e7eb" />
        <text x={30} y={10} textAnchor="end" fontSize={8} fill="#9ca3af">
          {maxCount}
        </text>
        <text
          x={30}
          y={chartHeight}
          textAnchor="end"
          fontSize={8}
          fill="#9ca3af"
        >
          0
        </text>

        {/* Bars */}
        {bins.map((bin, i) => {
          const barHeight =
            maxCount > 0 ? (bin.count / maxCount) * chartHeight : 0;
          const x = 35 + i * (barWidth + 1);
          const isP25 = bin.min <= dist.p25 && bin.max > dist.p25;
          const isMedian = bin.min <= dist.median && bin.max > dist.median;
          const isP75 = bin.min <= dist.p75 && bin.max > dist.p75;

          return (
            <g key={i}>
              <rect
                x={x}
                y={chartHeight - barHeight}
                width={barWidth}
                height={barHeight}
                fill={
                  isMedian ? "#4f46e5" : isP25 || isP75 ? "#818cf8" : "#c7d2fe"
                }
                rx={1}
              />
              <title>
                {bin.min}-{bin.max} {unitLabel}: {bin.count} patients
              </title>
            </g>
          );
        })}

        {/* X axis labels */}
        <text x={35} y={chartHeight + 12} fontSize={8} fill="#9ca3af">
          {dist.min}
          {unit}
        </text>
        <text
          x={35 + chartWidth}
          y={chartHeight + 12}
          fontSize={8}
          fill="#9ca3af"
          textAnchor="end"
        >
          {dist.max}
          {unit}
        </text>

        {/* Median marker */}
        <line
          x1={
            35 +
            ((dist.median - dist.min) / (dist.max - dist.min || 1)) * chartWidth
          }
          y1={0}
          x2={
            35 +
            ((dist.median - dist.min) / (dist.max - dist.min || 1)) * chartWidth
          }
          y2={chartHeight}
          stroke="#4f46e5"
          strokeDasharray="2,2"
          strokeWidth={1}
        />
      </svg>
    </div>
  );
};

const Stat = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <div>
    <span style={{ color: "#9ca3af" }}>{label}: </span>
    <span style={{ fontWeight: 600, color: color || "#111827" }}>{value}</span>
  </div>
);

export default PathwayAnalysis;
